import React, { Component } from "react";
import axios from "axios";
import {
  Card,
  Table,
  Button,
  Row,
  Col,
  Form,
  Spinner,
  Alert,
  Badge,
  Modal,
} from "react-bootstrap";
import CartContext from "./CartContext";
import LedgerEntryList from "./LedgerEntryList";
import BankStatementLineList from "./BankStatementLineList";

/* ===================== PAGE: LIST + ADD/EDIT/DELETE ===================== */

export default class BankReconciliationManagementPage extends Component {
  static contextType = CartContext;

  state = {
    // store (needed by loadAccounts)
    storeId: "",
    // accounts for dropdowns
    accounts: [],
    // list
    allItems: [], // master list from API
    items: [], // filtered list for table
    loading: false,
    error: "",
    success: "",
    // filters
    q: "",
    status: "",
    bankAccountId: "",
    // modal
    showModal: false,
    initial: null, // for edit, pass full record to modal
  };

  // ---- helper: consistent API base (adds /api/ when not provided) ----
  apiBase = () => {
    const base =
      this.props.apiBaseUrl ??
      process.env.REACT_APP_API ??
      (typeof window !== "undefined"
        ? window.location.origin + "/api/"
        : "/api/");
    return base.endsWith("/") ? base : base + "/";
  };

  componentDidMount() {
    const { storeId } = this.context;
    this.setState({ storeId: storeId || "" }, () => {
      this.loadAccounts();
      this.fetchList();
    });
  }

  /* ------------ Accounts (for filter + modal) ------------ */
  loadAccounts = () => {
    const { storeId, q } = this.state;
    if (!storeId) return;

    const API = this.apiBase();
    const url = `${API}BankAccount/Store/${storeId}`;
    const params = q ? { q } : {};

    axios
      .get(url, { params })
      .then((res) => this.setState({ accounts: res.data || [] }))
      .catch((err) => {
        console.error(err);
        this.setState({
          error:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to load bank accounts.",
        });
      });
  };

  /* --------------------- Helpers & Filters --------------------- */
  statusText = (val) => {
    if (typeof val === "string") return val; // tolerate string status from API
    const map = { 0: "Draft", 1: "InProgress", 2: "Completed" };
    return map[val ?? -1] || "Draft";
  };

  dateOnly = (s) => (s ? String(s).slice(0, 10) : "");

  applyFilters = () => {
    const { allItems, q, status, bankAccountId } = this.state;
    let filtered = allItems;

    if (bankAccountId) {
      filtered = filtered.filter(
        (x) => String(x.bankAccountId) === String(bankAccountId)
      );
    }

    if (status) {
      filtered = filtered.filter((x) => x.status === status);
    }

    if (q) {
      const ql = q.toLowerCase();
      filtered = filtered.filter((x) => {
        const bankName = x._raw?.bankAccount?.bankName || "";
        return (
          String(x.id || "")
            .toLowerCase()
            .includes(ql) ||
          bankName.toLowerCase().includes(ql) ||
          this.dateOnly(x.recoDate).includes(q) ||
          this.dateOnly(x.fromDate).includes(q) ||
          this.dateOnly(x.toDate).includes(q)
        );
      });
    }

    this.setState({ items: filtered });
  };

  /* --------------------- List --------------------- */
  fetchList = async () => {
    const { storeId } = this.state;
    if (!storeId) return;

    const API = this.apiBase();

    this.setState({ loading: true, error: "", success: "" });
    try {
      // Correct route casing & guaranteed /api/
      const { data } = await axios.get(
        `${API}BankReconciliation/Store/${storeId}`
      );
      const list = Array.isArray(data) ? data : [];

      // normalize once into UI-friendly shape; keep original on _raw
      const get = (o, a, b) => (o?.[a] !== undefined ? o[a] : o?.[b]);
      const normalized = list.map((r) => ({
        id: get(r, "id", "Id"),
        bankAccountId: get(r, "bankAccountId", "BankAccountId"),
        recoDate: get(r, "reconciliationDate", "ReconciliationDate"),
        fromDate: get(r, "ledgerStartDate", "LedgerStartDate"),
        toDate: get(r, "ledgerEndDate", "LedgerEndDate"),
        status: this.statusText(get(r, "status", "Status")),

        // counts coming directly from API
        ledgerEntriesCount:
          get(r, "ledgerEntriesCount", "LedgerEntriesCount") ?? 0,
        bankStatementLinesCount:
          get(r, "bankStatementLinesCount", "BankStatementLinesCount") ?? 0,
        matchesCount: get(r, "matchesCount", "MatchesCount") ?? 0,

        _raw: r,
      }));

      this.setState({ allItems: normalized }, this.applyFilters);
    } catch (err) {
      this.setState({
        error:
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          "Failed to load reconciliations.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /* --------------------- Add/Edit/Delete --------------------- */
  openNew = () =>
    this.setState({ showModal: true, initial: null, success: "", error: "" });

  openEdit = async (id) => {
    const { storeId } = this.state;
    const API = this.apiBase();
    this.setState({ error: "", success: "" });
    try {
      // Correct casing; pass storeId via params
      const { data } = await axios.get(`${API}BankReconciliation/Full/${id}`, {
        params: { storeId },
      });
      this.setState({ initial: data, showModal: true });
    } catch (err) {
      this.setState({
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to open reconciliation.",
      });
    }
  };

  deleteItem = async (id) => {
    const yes = window.confirm(
      "Delete this reconciliation? This cannot be undone."
    );
    if (!yes) return;
    const { storeId } = this.state;
    const API = this.apiBase();

    try {
      await axios.delete(`${API}BankReconciliation/${id}`, {
        params: { storeId },
      });
      this.setState({ success: "Reconciliation deleted." });
      this.fetchList();
    } catch (err) {
      this.setState({
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to delete reconciliation.",
      });
    }
  };

  /* --------------------- Render helpers --------------------- */
  bankName = (bankId) => {
    const { accounts } = this.state;
    const b = (accounts || []).find(
      (x) => String(x.Id ?? x.id) === String(bankId)
    );
    return b?.BankName || b?.bankName || b?.name || b?.accountName || "-";
  };

  statusBadge = (val) => {
    // normalize
    let s = val;
    if (typeof s === "number") {
      s = { 0: "Draft", 1: "InProgress", 2: "Completed" }[s] || "Draft";
    } else if (typeof s === "string") {
      const lower = s.toLowerCase();
      if (lower === "draft") s = "Draft";
      else if (lower === "inprogress" || lower === "in progress")
        s = "InProgress";
      else if (lower === "completed" || lower === "complete") s = "Completed";
      else s = "Draft";
    } else {
      s = "Draft";
    }

    // pick bootstrap variant
    const map = {
      Draft: "secondary",
      InProgress: "warning",
      Completed: "success",
    };
    const variant = map[s] || "secondary";

    return <Badge bg={variant}>{s}</Badge>;
  };

  renderToolbar() {
    const { q, status, bankAccountId, accounts, loading } = this.state;
    return (
      <Row className="g-2 align-items-end">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Search</Form.Label>
            <Form.Control
              placeholder="by bank, id, or date (yyyy-mm-dd)"
              value={q}
              onChange={(e) =>
                this.setState({ q: e.target.value }, () => {
                  this.loadAccounts(); // optional: keeps bank list filtered with q
                  this.applyFilters();
                })
              }
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Bank</Form.Label>
            <Form.Select
              value={bankAccountId}
              onChange={(e) =>
                this.setState(
                  { bankAccountId: e.target.value },
                  this.applyFilters
                )
              }
            >
              <option value="">All Banks</option>
              {(accounts || []).map((b) => (
                <option key={b.Id || b.id} value={b.Id || b.id}>
                  {b.BankName || b.bankName || b.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={status}
              onChange={(e) =>
                this.setState({ status: e.target.value }, this.applyFilters)
              }
            >
              <option value="">All</option>
              <option>Draft</option>
              <option>InProgress</option>
              <option>Completed</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Button
            className="w-100"
            variant="outline-secondary"
            onClick={this.fetchList}
            disabled={loading}
          >
            {loading ? <Spinner size="sm" animation="border" /> : "Refresh"}
          </Button>
        </Col>
        <Col md={2}>
          <Button className="w-100" onClick={this.openNew}>
            Add New Reconcile
          </Button>
        </Col>
      </Row>
    );
  }

  renderTable() {
    const { items, loading } = this.state;

    return (
      <Table striped hover responsive className="mb-0">
        <thead>
          <tr>
            <th>Reconcile Date</th>
            <th>Bank</th>
            <th>From</th>
            <th>To</th>
            <th>Status</th>
            <th className="text-end">Ledger</th>
            <th className="text-end">Statements</th>
            <th className="text-end">Matches</th>
            <th style={{ width: 180 }}></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={9} className="text-center py-4">
                <Spinner animation="border" />
              </td>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <td colSpan={9} className="text-center py-4 text-muted">
                No reconciliations found
              </td>
            </tr>
          ) : (
            items.map((r) => (
              <tr key={r.id}>
                <td>{(r.recoDate || "").slice(0, 10)}</td>
                <td>{this.bankName(r.bankAccountId)}</td>
                <td>{(r.fromDate || "").slice(0, 10)}</td>
                <td>{(r.toDate || "").slice(0, 10)}</td>
                <td>{this.statusBadge(r.status)}</td>
                <td className="text-end">{r.ledgerEntriesCount}</td>
                <td className="text-end">{r.bankStatementLinesCount}</td>
                <td className="text-end">{r.matchesCount}</td>
                <td className="text-end">
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => this.openEdit(r.id)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.deleteItem(r.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    );
  }

  render() {
    const { error, success, showModal, initial, accounts } = this.state;

    return (
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span className="fw-semibold">
            <h3>
              <b>Bank Reconciliation Management</b>
            </h3>
          </span>
        </Card.Header>
        <Card.Body>
          {error ? <Alert variant="danger">{error}</Alert> : null}
          {success ? <Alert variant="success">{success}</Alert> : null}

          {this.renderToolbar()}

          <div className="mt-3">{this.renderTable()}</div>
        </Card.Body>

        {/* Reconcile modal (controlled) */}
        {showModal && (
          <BankReconciliationManagement
            storeId={this.state.storeId}
            apiBaseUrl={this.props.apiBaseUrl}
            show={showModal}
            onHide={() => this.setState({ showModal: false })}
            initial={initial} // used for Add
            reconciliationId={this.state.editId} // used for Edit
            bankAccounts={accounts} // <-- dropdown options from loadAccounts()
            onSaved={() => {
              this.setState({
                showModal: false,
                success: "Reconciliation saved.",
              });
              this.fetchList();
            }}
          />
        )}
      </Card>
    );
  }
}

/* ===================== MODAL: RECONCILE ===================== */

export class BankReconciliationManagement extends Component {
  // static contextType = CartContext;

  state = {
    header: {
      id: null, // reconciliation id (Guid | null)
      storeId: this.props.storeId || null,
      bankAccountId: "",
      reconciliationDate: new Date().toISOString(),
      ledgerStartDate: "",
      ledgerEndDate: "",
      status: "Draft", // Draft | InProgress | Completed
    },
    ledgerEntries: [],
    bankStatementLines: [],
    selectedLedgerIds: new Set(),
    selectedStmtIds: new Set(),
    matches: [],

    loading: false,
    saving: false,
    error: "",
    success: "",
    csvLoading: false,
  };
  // Add inside the class (e.g., near other helpers)
  getLocalDateKey = (row) => {
    const raw = row?.txnDate ?? row?.TxnDate ?? row?.date ?? row?.Date ?? null;
    if (!raw) return "";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; // yyyy-mm-dd (LOCAL)
  };

  formatDateTime(value, { hideMidnight = false } = {}) {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return ""; // prevent NaN

    const pad = (n) => String(n).padStart(2, "0");
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = String(d.getFullYear()).slice(-2);
    const hh = d.getHours();
    const mm = d.getMinutes();

    if (hideMidnight && hh === 0 && mm === 0) {
      return `${day}-${month}-${year}`;
    }
    return `${day}-${month}-${year} ${pad(hh)}:${pad(mm)}`;
  }
  // format for display: "dd-MM-yy HH:mm"

  // preserve LOCAL time in an ISO-like string WITHOUT timezone "Z"
  localIso = (value) => {
    if (!value) return null;
    const s = String(value).trim();
    if (!s) return null;

    // If already ISO-ish, keep as-is (don't append Z)
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?Z?$/.test(s)) {
      return s.replace(/Z$/, ""); // drop Z to avoid server UTC conversion
    }

    const d = new Date(s);
    if (isNaN(d.getTime())) return null;

    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  componentDidMount() {
    // Prefer prop.storeId; fallback to context if present
    const incomingStoreId = this.props.storeId;

    // If a reconciliation Id is provided, load the full graph
    if (this.props.reconciliationId) {
      this.loadFull(this.props.apiBaseUrl, this.props.reconciliationId);
    } else if (this.props.initial) {
      // If parent passed initial data, normalize into our header + lists
      const init = this.props.initial;
      const statusText = (val) => {
        const map = { 0: "Draft", 1: "InProgress", 2: "Completed" };
        if (typeof val === "string") return val;
        return map[val ?? -1] || "Draft";
      };

      this.setState({
        header: {
          id: init.id || init.Id || null,
          storeId: incomingStoreId || init.storeId || init.StoreId || null,
          bankAccountId: init.bankAccountId || init.BankAccountId || "",
          reconciliationDate:
            init.reconciliationDate ||
            init.ReconciliationDate ||
            new Date().toISOString(),
          ledgerStartDate:
            init.ledgerStartDate || init.fromDate
              ? new Date(init.ledgerStartDate || init.fromDate).toISOString()
              : "",
          ledgerEndDate:
            init.ledgerEndDate || init.toDate
              ? new Date(init.ledgerEndDate || init.toDate).toISOString()
              : "",
          status: statusText(init.status),
        },
        ledgerEntries: init.ledgerEntries || [],
        bankStatementLines: init.bankStatementLines || [],
      });
    } else {
      // Fresh header
      this.setState((s) => ({
        header: {
          ...s.header,
          storeId: incomingStoreId || null,
        },
      }));
    }
  }

  // ---------- utilities ----------
  ensureApiBase = () => {
    const raw =
      this.props.apiBaseUrl ??
      process.env.REACT_APP_API ??
      (typeof window !== "undefined" ? window.location.origin + "/" : "/");

    // ensure trailing slash
    let base = raw.endsWith("/") ? raw : raw + "/";

    // make sure we end with "/api/"
    if (!/\/api\/$/i.test(base)) {
      if (/\/api$/i.test(base)) base += "/";
      else base += "api/";
    }
    return base; // always .../api/
  };

  isGuid = (s) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      String(s || "")
    );

  toIso = (d) => {
    if (!d) return null;
    const dt = typeof d === "string" ? new Date(d) : d;
    return isNaN(dt?.getTime?.()) ? null : dt.toISOString();
  };

  normalizeStatus = (val, fallback = "Draft") => {
    if (val === 0 || val === 1 || val === 2) {
      return { 0: "Draft", 1: "InProgress", 2: "Completed" }[val];
    }
    const s = String(val ?? "")
      .trim()
      .toLowerCase();
    if (!s) return fallback;
    if (s === "draft") return "Draft";
    if (s === "inprogress" || s === "in progress") return "InProgress";
    if (s === "completed" || s === "complete") return "Completed";
    return fallback;
  };

  statusToInt = (status) => {
    if (typeof status === "number") return status;
    const s = String(status || "")
      .toLowerCase()
      .replace(/\s+/g, "");
    if (s === "inprogress") return 1;
    if (s === "completed" || s === "complete") return 2;
    return 0; // draft
  };

  // ---------- header field handlers ----------
  setHeader = (key, value) => {
    this.setState((s) => ({
      header: { ...s.header, [key]: value },
      ...(key === "bankAccountId" ||
      key === "ledgerStartDate" ||
      key === "ledgerEndDate"
        ? { ledgerEntries: [], selectedLedgerIds: new Set(), matches: [] }
        : {}),
    }));
  };

  // ---------- API: Load full reconciliation ----------
  loadFull = async (apiBaseUrl, id) => {
    const base = apiBaseUrl ?? this.ensureApiBase();
    const url = `${base.replace(/\/$/, "")}/BankReconciliation/Full/${id}`;
    this.setState({ loading: true, error: "", success: "" });
    try {
      const { data } = await axios.get(url);
      this.setState({
        header: {
          id: data.id,
          storeId: data.storeId,
          bankAccountId: data.bankAccountId,
          reconciliationDate:
            data.reconciliationDate || new Date().toISOString(),
          ledgerStartDate: data.ledgerStartDate || "",
          ledgerEndDate: data.ledgerEndDate || "",
          status:
            { 0: "Draft", 1: "InProgress", 2: "Completed" }[data.status ?? 0] ||
            "Draft",
        },
        ledgerEntries: data.ledgerEntries || [],
        bankStatementLines: data.bankStatementLines || [],
        loading: false,
      });
    } catch (err) {
      this.setState({
        error:
          err?.response?.data?.message ||
          err?.response?.data?.title ||
          err?.message ||
          "Failed to load reconciliation.",
        loading: false,
      });
    }
  };

  // ---------- Save (POST SaveFull) ----------
  normalizeLedgerEntriesForApi = (rows) =>
    (rows || [])
      .map((r) => {
        const dateIso = this.localIso(
          r.txnDate ?? r.TxnDate ?? r.date ?? r.Date
        );
        const amt = Number(r.amount ?? r.Amount ?? 0);
        let dc = (r.debitCredit ?? r.DebitCredit ?? r.creditOrDebit ?? "")
          .toString()
          .trim()
          .toLowerCase();
        if (!dc) dc = amt < 0 ? "credit" : "debit";
        return {
          Id: r.id || r.Id || null,
          TxnDate: dateIso,
          Particulars: (r.particulars ?? r.Particulars ?? r.description ?? "")
            .toString()
            .trim(),
          Amount: Math.abs(isFinite(amt) ? amt : 0),
          DebitCredit: dc === "cr" ? "credit" : dc === "dr" ? "debit" : dc,
          IsCleared: Boolean(r.isCleared ?? r.IsCleared ?? false),
        };
      })
      .filter(
        (r) =>
          !!r.TxnDate &&
          r.Amount >= 0 &&
          (r.DebitCredit === "debit" || r.DebitCredit === "credit")
      );

  parseAmount = (v) => {
    if (v == null) return 0;
    let s = String(v).trim();
    let neg = false;

    // (123.45) => negative
    const paren = s.match(/^\((.*)\)$/);
    if (paren) {
      s = paren[1];
      neg = true;
    }

    // strip currency and thousand separators
    s = s.replace(/[₹$,]/g, "").replace(/\s+/g, "");

    // handle trailing CR/DR (e.g., 100CR / 100DR)
    const tail = s.match(/^(-?\d+(?:\.\d+)?)(cr|dr)$/i);
    if (tail)
      return /dr/i.test(tail[2]) ? -parseFloat(tail[1]) : parseFloat(tail[1]);

    const n = parseFloat(s);
    if (isNaN(n)) return 0;
    return neg ? -n : n;
  };

  normalizeDc = (raw, signed) => {
    const s = String(raw || "")
      .trim()
      .toLowerCase();
    if (["credit", "cr", "c"].includes(s)) return "credit";
    if (["debit", "dr", "d"].includes(s)) return "debit";
    if (Number.isFinite(signed)) return signed < 0 ? "debit" : "credit";
    return "debit"; // safe default
  };

  // Normalizer for bank statement lines (already using helpers above)
  normalizeStatementLinesForApi = (rows) =>
    (rows || [])
      .map((r) => {
        const dateIso =
          this.localIso(r.txnDate ?? r.TxnDate ?? r.date ?? r.Date) ?? null;

        const signedAmt = this.parseAmount(r.amount ?? r.Amount ?? 0);
        const dcRaw =
          r.debitCredit ??
          r.DebitCredit ??
          r.creditOrDebit ??
          r.CreditOrDebit ??
          "";
        const dc = this.normalizeDc(dcRaw, signedAmt); // "debit" | "credit"
        const amt = Math.abs(Number.isFinite(signedAmt) ? signedAmt : 0);

        const idCandidate = r.id ?? r.Id;
        const apiId = this.isGuid(idCandidate) ? idCandidate : null;

        return {
          ...(apiId ? { Id: apiId } : {}), // only send real GUIDs
          TxnDate: dateIso,
          Particulars: (r.particulars ?? r.Particulars ?? "").toString().trim(),
          Amount: amt,
          DebitCredit: dc,
          IsCleared: Boolean(
            r.isCleared ?? r.IsCleared ?? r.isMatched ?? r.IsMatched ?? false
          ),
        };
      })
      .filter(
        (x) =>
          !!x.TxnDate &&
          Number.isFinite(x.Amount) &&
          x.Amount >= 0 &&
          (x.DebitCredit === "debit" || x.DebitCredit === "credit")
      );

  handleSave = async () => {
    const base = this.ensureApiBase(); // must end with /api/
    const url = `${base}BankReconciliation/SaveFull`;

    const { header, ledgerEntries, bankStatementLines, matches } = this.state;

    // Trim IDs (selects can yield "")
    const storeId = (header.storeId || this.props.storeId || "").trim();
    const bankAccountId = (header.bankAccountId || "").trim();

    if (!this.isGuid(storeId))
      return this.setState({ error: "StoreId is invalid.", success: "" });
    if (!this.isGuid(bankAccountId))
      return this.setState({ error: "BankAccountId is invalid.", success: "" });

    // Dates → null when empty
    const toIsoOrNull = (d) => {
      const v = this.localIso(d);
      return v && v.length ? v : null;
    };

    // ---- NEW: apply matched flags to rows before normalizing ----
    const matchedLedgerIds = new Set(matches.map((m) => m.ledgerId));
    const matchedStmtIds = new Set(matches.map((m) => m.stmtId));

    const ledgerForApi = (ledgerEntries || []).map((e) => {
      const id = e.id ?? e.Id;
      return {
        ...e,
        isCleared: matchedLedgerIds.has(id) || !!e.isCleared || !!e.IsCleared, // preserve existing truthy
      };
    });

    const stmtForApi = (bankStatementLines || []).map((e) => {
      const id = e.id ?? e.Id;
      // We map isCleared -> IsMatched on the server side already
      return {
        ...e,
        isCleared:
          matchedStmtIds.has(id) ||
          !!e.isCleared ||
          !!e.IsCleared ||
          !!e.isMatched ||
          !!e.IsMatched,
      };
    });
    // ------------------------------------------------------------

    const payload = {
      Id: this.isGuid(header.id) ? header.id : null,
      StoreId: storeId,
      BankAccountId: bankAccountId,
      ReconciliationDate: toIsoOrNull(header.reconciliationDate),
      LedgerStartDate: toIsoOrNull(header.ledgerStartDate),
      LedgerEndDate: toIsoOrNull(header.ledgerEndDate),
      Status: this.statusToInt(header.status),
      LedgerEntries: this.normalizeLedgerEntriesForApi(ledgerForApi),
      BankStatementLines: this.normalizeStatementLinesForApi(stmtForApi),
    };

    this.setState({ saving: true, error: "", success: "" });
    try {
      const { data } = await axios.post(url, payload, {
        headers: { "Content-Type": "application/json" },
      });

      // Success: reflect the server graph
      this.setState({
        header: {
          id: data.id,
          storeId: data.storeId,
          bankAccountId: data.bankAccountId,
          reconciliationDate: data.reconciliationDate,
          ledgerStartDate: data.ledgerStartDate || "",
          ledgerEndDate: data.ledgerEndDate || "",
          // read both status & Status; accept number or string; fall back to user's selection
          status: this.normalizeStatus(
            data && (data.status ?? data.Status),
            this.state.header.status || "Draft"
          ),
        },
        ledgerEntries: data.ledgerEntries || [],
        bankStatementLines: data.bankStatementLines || [],
        saving: false,
        success: "Saved successfully.",
      });

      this.props.onSaved && this.props.onSaved(data.id);
    } catch (err) {
      // Show precise server errors (ModelState / ProblemDetails / string)
      let serverMsg = "";
      const rd = err?.response?.data;
      if (rd) {
        if (typeof rd === "string") serverMsg = rd;
        else if (rd.errors) {
          serverMsg = Object.entries(rd.errors)
            .map(([k, v]) => `${k}: ${(v || []).join(", ")}`)
            .join(" | ");
        } else serverMsg = rd.message || rd.title || JSON.stringify(rd);
      }
      const msg = serverMsg || err?.message || "Save failed.";
      this.setState({ saving: false, error: msg, success: "" });
    }
  };

  // ---------- matching (kept local; not persisted in API here) ----------
  toggleLedger = (id) =>
    this.setState((s) => {
      const set = new Set(s.selectedLedgerIds);
      set.has(id) ? set.delete(id) : set.add(id);
      return { selectedLedgerIds: set };
    });

  toggleStmt = (id) =>
    this.setState((s) => {
      const set = new Set(s.selectedStmtIds);
      set.has(id) ? set.delete(id) : set.add(id);
      return { selectedStmtIds: set };
    });

  isLedgerMatched = (id) => this.state.matches.some((m) => m.ledgerId === id);
  isStmtMatched = (id) => this.state.matches.some((m) => m.stmtId === id);

  matchSelected = () => {
    const {
      selectedLedgerIds,
      selectedStmtIds,
      matches,
      ledgerEntries,
      bankStatementLines,
    } = this.state;
    const l = Array.from(selectedLedgerIds);
    const s = Array.from(selectedStmtIds);
    if (l.length === 0 || s.length === 0)
      return this.setState({ error: "Select at least one on each side." });
    if (l.length !== s.length)
      return this.setState({
        error: "Selected counts differ. Select equal counts to pair.",
      });

    // Optional: enforce key equality even for manual matches
    const byId = (arr, id) => arr.find((x) => (x.id ?? x.Id) === id);
    const keyOf = (r) => {
      const dateKey = this.getLocalDateKey(r); // LOCAL date-only
      const signed = this.parseAmount(r.amount ?? r.Amount ?? 0);
      const amt = Math.abs(Number.isFinite(signed) ? signed : 0).toFixed(2);
      const dc = this.normalizeDc(
        r.debitCredit ??
          r.DebitCredit ??
          r.creditOrDebit ??
          r.CreditOrDebit ??
          "",
        signed
      );
      return `${dateKey}|${amt}|${dc}`;
    };

    const newPairs = [];
    for (let i = 0; i < l.length; i++) {
      const le = byId(ledgerEntries, l[i]);
      const st = byId(bankStatementLines, s[i]);
      if (!le || !st) continue;
      if (keyOf(le) !== keyOf(st)) continue; // only match if keys align
      newPairs.push({ ledgerId: l[i], stmtId: s[i] });
    }

    const existingKeys = new Set(
      matches.map((m) => `${m.ledgerId}|${m.stmtId}`)
    );
    const uniquePairs = newPairs.filter(
      (p) => !existingKeys.has(`${p.ledgerId}|${p.stmtId}`)
    );

    this.setState({
      matches: [...matches, ...uniquePairs],
      selectedLedgerIds: new Set(),
      selectedStmtIds: new Set(),
      success: `Matched ${uniquePairs.length} pair${
        uniquePairs.length === 1 ? "" : "s"
      }.`,
      error: "",
    });
  };

  /** AUTO MATCH: one-to-one by (Date + Amount + Credit/Debit), robust to field shapes */
  handleAutoMatch = () => {
    const { ledgerEntries, bankStatementLines, matches } = this.state;

    // Helper: get stable id; skip rows without id
    const getId = (r) => r?.id ?? r?.Id ?? null;

    const dateKey = (r) => this.getLocalDateKey(r); // LOCAL date-only

    const signedAmt = (r) => this.parseAmount(r?.amount ?? r?.Amount ?? 0);
    const amtKey = (r) =>
      Math.abs(Number.isFinite(signedAmt(r)) ? signedAmt(r) : 0).toFixed(2);
    const dcKey = (r) =>
      this.normalizeDc(
        r?.debitCredit ??
          r?.DebitCredit ??
          r?.creditOrDebit ??
          r?.CreditOrDebit ??
          "",
        signedAmt(r)
      );

    const keyOf = (r) => {
      const dkey = dateKey(r);
      const signed = this.parseAmount(r?.amount ?? r?.Amount ?? 0);
      const amt = Math.abs(Number.isFinite(signed) ? signed : 0).toFixed(2);
      const dc = this.normalizeDc(
        r?.debitCredit ??
          r?.DebitCredit ??
          r?.creditOrDebit ??
          r?.CreditOrDebit ??
          "",
        signed
      );
      return `${dkey}|${amt}|${dc}`;
    };

    // Track already matched ids
    const matchedLedgerIds = new Set(matches.map((m) => m.ledgerId));
    const matchedStmtIds = new Set(matches.map((m) => m.stmtId));

    // Build multimap for statements
    const stmtMap = new Map();
    bankStatementLines.forEach((s, idx) => {
      const sid = getId(s);
      if (!sid || matchedStmtIds.has(sid)) return;
      const k = keyOf(s);
      if (!stmtMap.has(k)) stmtMap.set(k, []);
      stmtMap.get(k).push(sid);
    });

    const newPairs = [];
    ledgerEntries.forEach((l) => {
      const lid = getId(l);
      if (!lid || matchedLedgerIds.has(lid)) return;
      const k = keyOf(l);
      const bucket = stmtMap.get(k);
      if (bucket && bucket.length) {
        const sid = bucket.shift(); // consume one statement id
        newPairs.push({ ledgerId: lid, stmtId: sid });
      }
    });

    if (!newPairs.length) {
      this.setState({ success: "", error: "No auto matches found." });
      return;
    }

    this.setState({
      matches: [...matches, ...newPairs],
      success: `Auto matched ${newPairs.length} pair${
        newPairs.length === 1 ? "" : "s"
      }.`,
      error: "",
    });
  };

  unmatchPair = (pair) => {
    this.setState((s) => ({
      matches: s.matches.filter(
        (m) => !(m.ledgerId === pair.ledgerId && m.stmtId === pair.stmtId)
      ),
    }));
  };

  formatMoney = (amt, cur = "INR") => {
    const num = typeof amt === "number" ? amt : Number(amt || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: cur,
        maximumFractionDigits: 2,
      }).format(num);
    } catch {
      return num.toFixed(2);
    }
  };

  computeSummary() {
    const { ledgerEntries, bankStatementLines, matches } = this.state;

    const L = new Map((ledgerEntries || []).map((e) => [e.id ?? e.Id, e]));
    const S = new Map((bankStatementLines || []).map((e) => [e.id ?? e.Id, e]));

    const getAmt = (o) => Math.abs(Number(o?.amount ?? o?.Amount ?? 0)) || 0;
    const getSide = (o) =>
      (
        o?.debitCredit ??
        o?.DebitCredit ??
        o?.creditOrDebit ??
        o?.CreditOrDebit ??
        ""
      )
        .toString()
        .trim()
        .toLowerCase();

    const sumBreakdown = (arr) =>
      arr.reduce(
        (acc, e) => {
          const amt = getAmt(e);
          const side = getSide(e);
          if (side === "credit") acc.cr += amt;
          else if (side === "debit") acc.dr += amt;
          else acc.unk += amt;
          return acc;
        },
        { cr: 0, dr: 0, unk: 0 }
      );

    const ledgerBreak = sumBreakdown(ledgerEntries || []);
    const stmtBreak = sumBreakdown(bankStatementLines || []);

    const totalLedgerCount = (ledgerEntries || []).length;
    const totalLedgerSum = ledgerBreak.cr + ledgerBreak.dr;

    const totalStmtCount = (bankStatementLines || []).length;
    const totalStmtSum = stmtBreak.cr + stmtBreak.dr;

    // valid matches only
    const validPairs = (matches || []).filter(
      (m) => L.has(m.ledgerId) && S.has(m.stmtId)
    );

    const matchedCount = validPairs.length;
    const matchedLedgerSum = validPairs.reduce(
      (a, m) => a + getAmt(L.get(m.ledgerId)),
      0
    );
    const matchedStmtSum = validPairs.reduce(
      (a, m) => a + getAmt(S.get(m.stmtId)),
      0
    );

    const unmatchedLedgerCount = totalLedgerCount - matchedCount;
    const unmatchedStmtCount = totalStmtCount - matchedCount;

    const unmatchedLedgerSum = totalLedgerSum - matchedLedgerSum;
    const unmatchedStmtSum = totalStmtSum - matchedStmtSum;

    const amountDiff = unmatchedLedgerSum - unmatchedStmtSum;

    return {
      ledgerBreak,
      stmtBreak,
      totalLedgerCount,
      totalLedgerSum,
      totalStmtCount,
      totalStmtSum,
      matchedCount,
      matchedLedgerSum,
      matchedStmtSum,
      unmatchedLedgerCount,
      unmatchedStmtCount,
      unmatchedLedgerSum,
      unmatchedStmtSum,
      amountDiff,
    };
  }

  balanceBadge(amountDiff, cur = "INR") {
    const ok = Math.abs(Number(amountDiff || 0)) < 0.005; // ~₹0.01 tolerance
    const label = ok ? "Balanced" : "Out of Balance";
    const variant = ok ? "success" : "danger";
    // show the signed difference only when not balanced
    const extra = ok ? "" : ` — ${this.formatMoney(amountDiff, cur)}`;
    return (
      <Badge bg={variant} className="ms-2">
        {label}
        {extra}
      </Badge>
    );
  }

  renderSummary() {
    const cur = "INR";
    const s = this.computeSummary();
    return (
      <Card className="mt-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span className="fw-semibold">Reconciliation Summary</span>
          {this.balanceBadge(s.amountDiff, cur)}
        </Card.Header>
        <Card.Body className="p-0">
          <Table striped hover responsive className="mb-0">
            <thead>
              <tr>
                <th></th>
                <th className="text-end">Count</th>
                <th className="text-end">Credits</th>
                <th className="text-end">Debits</th>
                <th className="text-end">Net</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Ledger (Total)</strong>
                </td>
                <td className="text-end">{s.totalLedgerCount}</td>
                <td className="text-end">
                  {this.formatMoney(s.ledgerBreak.cr, cur)}
                </td>
                <td className="text-end">
                  {this.formatMoney(s.ledgerBreak.dr, cur)}
                </td>
                <td className="text-end">
                  {this.formatMoney(s.ledgerBreak.cr - s.ledgerBreak.dr, cur)}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Bank Statement (Total)</strong>
                </td>
                <td className="text-end">{s.totalStmtCount}</td>
                <td className="text-end">
                  {this.formatMoney(s.stmtBreak.cr, cur)}
                </td>
                <td className="text-end">
                  {this.formatMoney(s.stmtBreak.dr, cur)}
                </td>
                <td className="text-end">
                  {this.formatMoney(s.stmtBreak.cr - s.stmtBreak.dr, cur)}
                </td>
              </tr>
              <tr>
                <td>Matched</td>
                <td className="text-end">{s.matchedCount}</td>
                <td colSpan={3} className="text-end">
                  {this.formatMoney(s.matchedLedgerSum, cur)} ↔{" "}
                  {this.formatMoney(s.matchedStmtSum, cur)}
                </td>
              </tr>
              <tr>
                <td>Unmatched (Ledger)</td>
                <td className="text-end">{s.unmatchedLedgerCount}</td>
                <td colSpan={3} className="text-end">
                  {this.formatMoney(s.unmatchedLedgerSum, cur)}
                </td>
              </tr>
              <tr>
                <td>Unmatched (Bank Statement)</td>
                <td className="text-end">{s.unmatchedStmtCount}</td>
                <td colSpan={3} className="text-end">
                  {this.formatMoney(s.unmatchedStmtSum, cur)}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th>Amount Difference (Unmatched L − Unmatched S)</th>
                <th></th>
                <th colSpan={3} className="text-end">
                  {this.formatMoney(s.amountDiff, cur)}
                </th>
              </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>
    );
  }

  // ---------- UI ----------
  renderHeaderForm() {
    const { bankAccounts = [] } = this.props;
    const { header } = this.state;

    const toDateOnly = (s) => (s ? String(s).slice(0, 10) : "");

    return (
      <Card className="mb-3">
        <Card.Header className="fw-semibold">Reconcile Details</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Date</Form.Label>+{" "}
                <Form.Control
                  type="datetime-local"
                  value={String(header.reconciliationDate || "").slice(0, 16)}
                  onChange={(e) =>
                    this.setHeader("reconciliationDate", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Bank</Form.Label>
                <Form.Select
                  value={header.bankAccountId}
                  onChange={(e) =>
                    this.setHeader("bankAccountId", e.target.value)
                  }
                >
                  <option value="">-- Select Bank --</option>
                  {bankAccounts.map((b) => {
                    const id = String(b.Id ?? b.id ?? "");
                    const name =
                      b.BankName ??
                      b.bankName ??
                      b.name ??
                      b.accountName ??
                      "Bank";
                    return (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>From</Form.Label>
                <Form.Control
                  type="date"
                  value={toDateOnly(header.ledgerStartDate)}
                  onChange={(e) =>
                    this.setHeader(
                      "ledgerStartDate",
                      e.target.value
                        ? new Date(e.target.value).toISOString()
                        : ""
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>To</Form.Label>
                <Form.Control
                  type="date"
                  value={toDateOnly(header.ledgerEndDate)}
                  onChange={(e) =>
                    this.setHeader(
                      "ledgerEndDate",
                      e.target.value
                        ? new Date(e.target.value).toISOString()
                        : ""
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={1}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={header.status}
                  onChange={(e) => this.setHeader("status", e.target.value)}
                >
                  <option>Draft</option>
                  <option>InProgress</option>
                  <option>Completed</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  }

  renderPairs() {
    const { matches, ledgerEntries, bankStatementLines } = this.state;
    if (matches.length === 0) return null;
    const L = new Map((ledgerEntries || []).map((e) => [e.id || e.Id, e]));
    const S = new Map((bankStatementLines || []).map((e) => [e.id || e.Id, e]));
    const pick = (obj, pascal, camel) => obj?.[pascal] ?? obj?.[camel];

    // NEW: safe date picker (resilient to casing & missing fields)
    const pickDate = (o) =>
      o?.TxnDate ?? o?.txnDate ?? o?.Date ?? o?.date ?? "";

    // NEW: safe amount
    const getAmt = (o) => Number(o?.Amount ?? o?.amount ?? 0).toFixed(2);

    return (
      <Card className="mt-2">
        <Card.Header className="fw-semibold">Matched Pairs</Card.Header>
        <Card.Body className="p-0">
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th>Ledger Date</th>
                <th>Ledger Particulars</th>
                <th className="text-end">Ledger Amount</th>
                <th></th>
                <th>Stmt Date</th>
                <th>Stmt Particulars</th>
                <th className="text-end">Stmt Amount</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m, idx) => {
                const le = L.get(m.ledgerId);
                const st = S.get(m.stmtId);
                if (!le || !st) return null;

                return (
                  <tr key={`${m.ledgerId}|${m.stmtId}|${idx}`}>
                    <td>{this.formatDateTime(pickDate(le))}</td>
                    <td>{pick(le, "Particulars", "particulars")}</td>
                    <td className="text-end">{getAmt(le)}</td>
                    <td className="text-center">↔</td>
                    <td>{this.formatDateTime(pickDate(st))}</td>
                    <td>{pick(st, "Particulars", "particulars")}</td>
                    <td className="text-end">{getAmt(st)}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => this.unmatchPair(m)}
                      >
                        Unmatch
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card.Body>
      </Card>
    );
  }

  render() {
    const { show, onHide, bankAccounts } = this.props;
    const {
      header,
      ledgerEntries,
      bankStatementLines,
      loading,
      saving,
      selectedLedgerIds,
      selectedStmtIds,
      error,
      success,
    } = this.state;

    return (
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        backdrop="static"
        dialogClassName="BankReconcileModal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Bank Reconciliation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error ? (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          ) : null}
          {success ? (
            <Alert variant="success" className="mb-3">
              {success}
            </Alert>
          ) : null}

          {this.renderHeaderForm(bankAccounts)}

          <div className="d-flex align-items-center gap-2 mb-2">
            <Badge bg="info">Matched Pairs: {this.state.matches.length}</Badge>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={this.matchSelected}
            >
              Match Selected
            </Button>
            <Button
              variant="primary"
              onClick={this.handleAutoMatch}
              className="ms-2"
            >
              Auto Match
            </Button>
          </div>

          <Row className="g-3">
            <Col md={6}>
              <LedgerEntryList
                apiBaseUrl={this.props.apiBaseUrl}
                storeId={header.storeId}
                fromDate={header.ledgerStartDate || header.reconciliationDate}
                toDate={header.ledgerEndDate || header.reconciliationDate}
                currency="INR"
                initialEntries={ledgerEntries}
                onLoaded={(entries) =>
                  this.setState({
                    ledgerEntries: entries,
                    selectedLedgerIds: new Set(),
                    matches: [],
                  })
                }
                selectedIds={selectedLedgerIds}
                onToggle={this.toggleLedger}
                isRowMatched={this.isLedgerMatched}
              />
            </Col>

            <Col md={6}>
              <BankStatementLineList
                apiBaseUrl={this.props.apiBaseUrl}
                storeId={header.storeId}
                currency="INR"
                lines={bankStatementLines}
                onCsvLoaded={(rows) =>
                  this.setState({
                    bankStatementLines: rows,
                    selectedStmtIds: new Set(),
                    matches: [],
                    success: `Loaded ${rows.length} line${
                      rows.length === 1 ? "" : "s"
                    } from CSV.`,
                  })
                }
                onClear={() =>
                  this.setState({
                    bankStatementLines: [],
                    selectedStmtIds: new Set(),
                    matches: [],
                  })
                }
                selectedIds={selectedStmtIds}
                onToggle={this.toggleStmt}
                isRowMatched={this.isStmtMatched}
              />
            </Col>
          </Row>
          {this.renderSummary()}
          {this.renderPairs()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
          <Button variant="primary" onClick={this.handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
